import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useGetBulkPartFileThumbnails } from './useGetBulkPartFileThumbnails';
import type { PartThumbnailImageData } from './usePartThumbnailImage/types';
import { getPartThumbnailImageQueryKey } from './usePartThumbnailImage/util';

const DEFAULT_BATCH_SIZE = 50;

function base64ToBlob(base64: string, contentType = '', sliceSize = 512): Blob {
  const byteCharacters = atob(base64);
  const byteArrays: BlobPart[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
}

export type ThumbnailBlobMap = Record<string, Blob | null>;

type UseBulkThumbnailLoadOptions = {
  /** Max UUIDs per request. Larger lists are split into concurrent batches. */
  batchSize?: number;
};

export const useBulkThumbnailLoad = ({
  batchSize = DEFAULT_BATCH_SIZE,
}: UseBulkThumbnailLoadOptions = {}) => {
  const queryClient = useQueryClient();
  const [thumbnailMap, setThumbnailMap] = useState<ThumbnailBlobMap>({});
  const [isBatching, setIsBatching] = useState(false);
  const abortRef = useRef(false);

  const { mutateAsync, isLoading: isMutationLoading } =
    useGetBulkPartFileThumbnails({
      queryOptions: {
        onSuccess: (data, params) => {
          const newEntries: ThumbnailBlobMap = {};
          const seenUuids = new Set<string>();

          for (const [uuid, encoded] of Object.entries(data.thumbnails)) {
            seenUuids.add(uuid);
            const blob = encoded ? base64ToBlob(encoded, 'image/png') : null;
            newEntries[uuid] = blob;

            // Hydrate the individual thumbnail query cache for cross-app reuse
            const queryKey = getPartThumbnailImageQueryKey({
              partFileUuid: uuid,
            });
            queryClient.setQueryData<PartThumbnailImageData>(queryKey, {
              thumbnailBlob: blob,
            });
          }

          // Mark any requested UUIDs not in response as null
          for (const uuid of params.uuids) {
            if (!seenUuids.has(uuid)) {
              newEntries[uuid] = null;
              const queryKey = getPartThumbnailImageQueryKey({
                partFileUuid: uuid,
              });
              queryClient.setQueryData<PartThumbnailImageData>(queryKey, {
                thumbnailBlob: null,
              });
            }
          }

          setThumbnailMap((prev) => ({ ...prev, ...newEntries }));
        },
      },
    });

  const loadThumbnails = useCallback(
    async (uuids: string[]) => {
      const cachedEntries: ThumbnailBlobMap = {};
      const uncachedUuids: string[] = [];

      for (const uuid of uuids) {
        const queryKey = getPartThumbnailImageQueryKey({
          partFileUuid: uuid,
        });
        const cached =
          queryClient.getQueryData<PartThumbnailImageData>(queryKey);

        if (cached) {
          cachedEntries[uuid] = cached.thumbnailBlob;
        } else {
          uncachedUuids.push(uuid);
        }
      }

      // Immediately populate the map with any cached entries
      if (Object.keys(cachedEntries).length > 0) {
        setThumbnailMap((prev) => ({ ...prev, ...cachedEntries }));
      }

      if (uncachedUuids.length === 0) return;

      // Reset abort flag for this load sequence
      abortRef.current = false;

      // Split into batches and fire all concurrently
      const batches: string[][] = [];
      for (let i = 0; i < uncachedUuids.length; i += batchSize) {
        batches.push(uncachedUuids.slice(i, i + batchSize));
      }

      setIsBatching(true);

      // Each batch's onSuccess fires independently as it resolves,
      // progressively merging results into the thumbnail map
      await Promise.allSettled(
        batches.map((batch) =>
          abortRef.current ? Promise.resolve() : mutateAsync({ uuids: batch }),
        ),
      );

      setIsBatching(false);
    },
    [mutateAsync, queryClient, batchSize],
  );

  // Cancel any in-flight batch sequence (e.g. when filters change)
  const cancelBatchLoad = useCallback(() => {
    abortRef.current = true;
  }, []);

  const isLoading = isMutationLoading || isBatching;

  return { loadThumbnails, cancelBatchLoad, thumbnailMap, isLoading };
};
